import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import formService from '../../services/formService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { Badge } from '../../components/ui/Badge';
import EmployerHeaderNav from '../../components/navigation/EmployerHeaderNav';

// Question type options
const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text', icon: 'Type', description: 'Single line text input' },
  { value: 'textarea', label: 'Long Text', icon: 'AlignLeft', description: 'Multi-line text area' },
  { value: 'url', label: 'URL', icon: 'Link', description: 'Website link with validation' },
  { value: 'dropdown', label: 'Dropdown', icon: 'ChevronDown', description: 'Select one from dropdown' },
  { value: 'radio', label: 'Multiple Choice', icon: 'Circle', description: 'Select one from visible options' },
  { value: 'checkbox', label: 'Checkboxes', icon: 'CheckSquare', description: 'Select multiple options' }
];

const FormBuilder = () => {
  const { formId } = useParams();
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([]);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // UI state
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  
  // Question editor state
  const [questionType, setQuestionType] = useState('text');
  const [questionText, setQuestionText] = useState('');
  const [questionRequired, setQuestionRequired] = useState(true);
  const [questionPlaceholder, setQuestionPlaceholder] = useState('');
  const [questionHelpText, setQuestionHelpText] = useState('');
  const [questionOptions, setQuestionOptions] = useState(['']);

  // Load form if editing
  const loadForm = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await formService.getFormById(formId, user.id);
      if (result.success) {
        const form = result.data;
        setTitle(form.title);
        setDescription(form.description || '');
        setQuestions(form.questions || []);
        setStatus(form.status);
      } else {
        setError(result.error || 'Failed to load form');
      }
    } catch (err) {
      console.error('Error loading form:', err);
      setError('Failed to load form. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [formId, user]);

  useEffect(() => {
    if (formId && user?.id) {
      loadForm();
    }
  }, [formId, user?.id, loadForm]);

  const handleSaveDraft = async () => {
    if (!title.trim()) {
      setError('Form title is required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const formData = {
        title: title.trim(),
        description: description.trim(),
        questions,
        employer_id: user.id,
        status: 'draft'
      };

      let result;
      if (formId) {
        result = await formService.updateForm(formId, formData, user.id);
      } else {
        result = await formService.createForm(formData);
      }

      if (result.success) {
        setSuccessMessage('Draft saved successfully');
        if (!formId && result.data?.id) {
          // Navigate to edit URL for newly created form
          navigate(`/form-builder/${result.data.id}`, { replace: true });
        }
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error || 'Failed to save draft');
      }
    } catch (err) {
      console.error('Error saving draft:', err);
      setError('Failed to save draft. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!title.trim()) {
      setError('Form title is required');
      return;
    }

    if (questions.length === 0) {
      setError('Add at least one question before publishing');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');
    setShowPublishConfirm(false);

    try {
      const formData = {
        title: title.trim(),
        description: description.trim(),
        questions,
        employer_id: user.id,
        status: 'published'
      };

      let result;
      if (formId) {
        result = await formService.updateForm(formId, formData, user.id);
      } else {
        result = await formService.createForm(formData);
      }

      if (result.success) {
        setSuccessMessage('Form published successfully');
        setStatus('published');
        if (!formId && result.data?.id) {
          navigate(`/form-builder/${result.data.id}`, { replace: true });
        }
        setTimeout(() => {
          navigate('/employer-dashboard?tab=forms');
        }, 1500);
      } else {
        setError(result.error || 'Failed to publish form');
      }
    } catch (err) {
      console.error('Error publishing form:', err);
      setError('Failed to publish form. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    setSaving(true);
    setError('');

    try {
      const result = await formService.unpublishForm(formId, user.id);
      if (result.success) {
        setStatus('draft');
        setSuccessMessage('Form unpublished successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error || 'Failed to unpublish form');
      }
    } catch (err) {
      console.error('Error unpublishing form:', err);
      setError('Failed to unpublish form. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const openQuestionModal = (question = null, index = null) => {
    if (question) {
      // Editing existing question
      setEditingQuestion(question);
      setEditingQuestionIndex(index);
      setQuestionType(question.type);
      setQuestionText(question.question);
      setQuestionRequired(question.required);
      setQuestionPlaceholder(question.placeholder || '');
      setQuestionHelpText(question.helpText || '');
      setQuestionOptions(question.options || ['']);
    } else {
      // Adding new question
      resetQuestionEditor();
    }
    setShowQuestionModal(true);
  };

  const resetQuestionEditor = () => {
    setEditingQuestion(null);
    setEditingQuestionIndex(null);
    setQuestionType('text');
    setQuestionText('');
    setQuestionRequired(true);
    setQuestionPlaceholder('');
    setQuestionHelpText('');
    setQuestionOptions(['']);
  };

  const handleSaveQuestion = () => {
    if (!questionText.trim()) {
      setError('Question text is required');
      return;
    }

    // Validate choice-based questions have options
    if (['dropdown', 'radio', 'checkbox'].includes(questionType)) {
      const validOptions = questionOptions.filter(opt => opt.trim() !== '');
      if (validOptions.length < 2) {
        setError('Choice-based questions must have at least 2 options');
        return;
      }
    }

    const newQuestion = {
      id: editingQuestion?.id || `q-${Date.now()}`,
      type: questionType,
      question: questionText.trim(),
      required: questionRequired,
      order: editingQuestionIndex !== null ? editingQuestionIndex : questions.length,
      placeholder: questionPlaceholder.trim() || undefined,
      helpText: questionHelpText.trim() || undefined,
      options: ['dropdown', 'radio', 'checkbox'].includes(questionType) 
        ? questionOptions.filter(opt => opt.trim() !== '')
        : undefined
    };

    if (editingQuestionIndex !== null) {
      // Update existing question
      const updatedQuestions = [...questions];
      updatedQuestions[editingQuestionIndex] = newQuestion;
      setQuestions(updatedQuestions);
    } else {
      // Add new question
      setQuestions([...questions, newQuestion]);
    }

    setShowQuestionModal(false);
    resetQuestionEditor();
    setError('');
  };

  const handleDeleteQuestion = (index) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    setQuestions(updatedQuestions);
  };

  const handleAddOption = () => {
    setQuestionOptions([...questionOptions, '']);
  };

  const handleUpdateOption = (index, value) => {
    const updated = [...questionOptions];
    updated[index] = value;
    setQuestionOptions(updated);
  };

  const handleRemoveOption = (index) => {
    if (questionOptions.length > 1) {
      setQuestionOptions(questionOptions.filter((_, i) => i !== index));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <EmployerHeaderNav />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <EmployerHeaderNav />
      <main className="content-container pt-16 pb-20">
        <div className="container max-w-7xl">
          {/* Header */}
          <div className="flex flex-col lg:flex-row items-center justify-between mb-6 space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/employer-dashboard?tab=forms')}
                className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  {formId ? 'Edit Form' : 'Create New Form'}
                </h1>
                <p className="text-sm text-text-secondary mt-1">
                  Build custom application forms to gather information from VAs
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Badge variant={status === 'published' ? 'success' : 'default'}>
                {status === 'published' ? 'Published' : 'Draft'}
              </Badge>
              {status === 'published' && (
                <Button
                  variant="outline"
                  iconName="EyeOff"
                  onClick={handleUnpublish}
                  disabled={saving}
                >
                  Unpublish
                </Button>
              )}
              <Button
                variant="outline"
                iconName="Save"
                onClick={handleSaveDraft}
                disabled={saving}
              >
                Save Draft
              </Button>
              <Button
                variant="primary"
                iconName="Eye"
                onClick={() => setShowPublishConfirm(true)}
                disabled={saving || questions.length === 0}
              >
                Publish Form
              </Button>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-error-50 border border-error text-error rounded-lg p-4 mb-6 flex items-start">
              <Icon name="AlertCircle" size={20} className="mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {successMessage && (
            <div className="bg-success-50 border border-success text-success rounded-lg p-4 mb-6 flex items-start">
              <Icon name="CheckCircle" size={20} className="mr-2 flex-shrink-0 mt-0.5" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Form Builder Layout */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Editor Column */}
            <div className="xl:col-span-2 space-y-6">
              {/* Form Title & Description */}
              <div className="bg-surface rounded-lg border border-border p-6">
                <h2 className="text-lg font-semibold text-text-primary mb-4">Form Details</h2>
                <div className="space-y-4">
                  <Input
                    label="Form Title"
                    placeholder="e.g., Social Media Manager Application Questions"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    maxLength={200}
                  />
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Description (Optional)
                    </label>
                    <textarea
                      placeholder="Brief description of what this form is for..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={1000}
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Questions List */}
              <div className="bg-surface rounded-lg border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-text-primary">
                    Questions ({questions.length})
                  </h2>
                  <Button
                    variant="primary"
                    iconName="Plus"
                    size="sm"
                    onClick={() => openQuestionModal()}
                  >
                    Add Question
                  </Button>
                </div>

                {questions.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="FileQuestion" size={32} className="text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-text-primary mb-2">
                      No questions yet
                    </h3>
                    <p className="text-text-secondary mb-4">
                      Add your first question to start building your form
                    </p>
                    <Button variant="primary" iconName="Plus" onClick={() => openQuestionModal()}>
                      Add First Question
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {questions.map((question, index) => (
                      <div
                        key={question.id}
                        className="border border-border rounded-lg p-4 hover:border-primary transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <Icon 
                                name={QUESTION_TYPES.find(t => t.value === question.type)?.icon || 'HelpCircle'} 
                                size={18} 
                                className="text-primary" 
                              />
                              <span className="text-xs font-medium text-text-secondary uppercase">
                                {QUESTION_TYPES.find(t => t.value === question.type)?.label || question.type}
                              </span>
                              {question.required && (
                                <Badge variant="error" size="sm">Required</Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium text-text-primary mb-1">
                              {index + 1}. {question.question}
                            </p>
                            {question.helpText && (
                              <p className="text-xs text-text-secondary">
                                {question.helpText}
                              </p>
                            )}
                            {question.options && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {question.options.map((opt, i) => (
                                  <span key={i} className="text-xs bg-secondary-100 text-text-secondary px-2 py-1 rounded">
                                    {opt}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => openQuestionModal(question, index)}
                              className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                              title="Edit question"
                            >
                              <Icon name="Edit" size={16} className="text-text-secondary" />
                            </button>
                            <button
                              onClick={() => handleDeleteQuestion(index)}
                              className="p-2 hover:bg-error-50 rounded-lg transition-colors"
                              title="Delete question"
                            >
                              <Icon name="Trash2" size={16} className="text-error" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Preview Column */}
            <div className="xl:col-span-1">
              <div className="bg-surface rounded-lg border border-border p-6 sticky top-20">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-text-primary">Preview</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName={showPreview ? 'EyeOff' : 'Eye'}
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    {showPreview ? 'Hide' : 'Show'}
                  </Button>
                </div>

                {showPreview && (
                  <div className="space-y-4">
                    {title && (
                      <div>
                        <h3 className="font-semibold text-text-primary mb-1">{title}</h3>
                        {description && (
                          <p className="text-sm text-text-secondary">{description}</p>
                        )}
                      </div>
                    )}

                    {questions.length > 0 ? (
                      <div className="space-y-4 pt-4 border-t border-border">
                        {questions.map((question, index) => (
                          <div key={question.id} className="space-y-2">
                            <label className="block text-sm font-medium text-text-primary">
                              {index + 1}. {question.question}
                              {question.required && <span className="text-error ml-1">*</span>}
                            </label>
                            {question.helpText && (
                              <p className="text-xs text-text-secondary">{question.helpText}</p>
                            )}
                            
                            {/* Preview input based on type */}
                            {question.type === 'text' && (
                              <input
                                type="text"
                                placeholder={question.placeholder}
                                disabled
                                className="w-full px-3 py-2 border border-border rounded-lg bg-secondary-50"
                              />
                            )}
                            {question.type === 'textarea' && (
                              <textarea
                                placeholder={question.placeholder}
                                disabled
                                rows={3}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-secondary-50"
                              />
                            )}
                            {question.type === 'url' && (
                              <input
                                type="url"
                                placeholder={question.placeholder || 'https://'}
                                disabled
                                className="w-full px-3 py-2 border border-border rounded-lg bg-secondary-50"
                              />
                            )}
                            {question.type === 'dropdown' && (
                              <select disabled className="w-full px-3 py-2 border border-border rounded-lg bg-secondary-50">
                                <option>Select an option...</option>
                                {question.options?.map((opt, i) => (
                                  <option key={i}>{opt}</option>
                                ))}
                              </select>
                            )}
                            {question.type === 'radio' && (
                              <div className="space-y-2">
                                {question.options?.map((opt, i) => (
                                  <label key={i} className="flex items-center space-x-2">
                                    <input type="radio" disabled name={`q-${question.id}`} className="text-primary" />
                                    <span className="text-sm">{opt}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                            {question.type === 'checkbox' && (
                              <div className="space-y-2">
                                {question.options?.map((opt, i) => (
                                  <label key={i} className="flex items-center space-x-2">
                                    <input type="checkbox" disabled className="text-primary" />
                                    <span className="text-sm">{opt}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-text-secondary italic">
                        Add questions to see the form preview
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Question Editor Modal */}
      {showQuestionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-text-primary">
                {editingQuestion ? 'Edit Question' : 'Add Question'}
              </h2>
              <button
                onClick={() => {
                  setShowQuestionModal(false);
                  resetQuestionEditor();
                  setError('');
                }}
                className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Question Type */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Question Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {QUESTION_TYPES.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setQuestionType(type.value)}
                      className={`p-3 border rounded-lg text-left transition-all ${
                        questionType === type.value
                          ? 'border-primary bg-primary-50'
                          : 'border-border hover:border-primary'
                      }`}
                    >
                      <div className="flex items-start space-x-2">
                        <Icon name={type.icon} size={20} className="text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-medium text-sm">{type.label}</div>
                          <div className="text-xs text-text-secondary">{type.description}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Question Text */}
              <Input
                label="Question"
                placeholder="Enter your question..."
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                required
                maxLength={500}
              />

              {/* Required Toggle */}
              <div className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                <div>
                  <div className="font-medium text-sm text-text-primary">Required Question</div>
                  <div className="text-xs text-text-secondary">VAs must answer this question</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={questionRequired}
                    onChange={(e) => setQuestionRequired(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* Placeholder (for text/textarea/url) */}
              {['text', 'textarea', 'url'].includes(questionType) && (
                <Input
                  label="Placeholder Text (Optional)"
                  placeholder="e.g., Enter your answer here..."
                  value={questionPlaceholder}
                  onChange={(e) => setQuestionPlaceholder(e.target.value)}
                />
              )}

              {/* Help Text */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Help Text (Optional)
                </label>
                <textarea
                  placeholder="Additional guidance for this question..."
                  value={questionHelpText}
                  onChange={(e) => setQuestionHelpText(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              {/* Options (for dropdown/radio/checkbox) */}
              {['dropdown', 'radio', 'checkbox'].includes(questionType) && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Options (at least 2 required)
                  </label>
                  <div className="space-y-2">
                    {questionOptions.map((option, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Input
                          placeholder={`Option ${index + 1}`}
                          value={option}
                          onChange={(e) => handleUpdateOption(index, e.target.value)}
                          className="flex-1"
                        />
                        {questionOptions.length > 1 && (
                          <button
                            onClick={() => handleRemoveOption(index)}
                            className="p-2 hover:bg-error-50 rounded-lg transition-colors"
                          >
                            <Icon name="X" size={18} className="text-error" />
                          </button>
                        )}
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Plus"
                      onClick={handleAddOption}
                      className="w-full"
                    >
                      Add Option
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setShowQuestionModal(false);
                  resetQuestionEditor();
                  setError('');
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveQuestion}>
                {editingQuestion ? 'Update Question' : 'Add Question'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {showPublishConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg p-6 max-w-md w-full">
            <div className="flex items-start space-x-4 mb-4">
              <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="Eye" size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-1">
                  Publish Form
                </h3>
                <p className="text-sm text-text-secondary">
                  Publishing this form will make it available to attach to your job postings. 
                  You can edit it later, but changes will only apply to new applications.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowPublishConfirm(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handlePublish}>
                Publish Form
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormBuilder;
